import { db } from '@app/icare/client/db'
import {
  localPaginatedQueryOptions,
  type ResultTypeFromQueryResult,
} from '@app/icare/client/db/client/localQueryOptions'
import { sync } from '@app/icare/client/sync'
import { FormStatusBadge } from '@app/icare/components/status/FormStatusBadge'
import { queryKit } from '@app/icare/db/query/queryKit'
import { PatientOrderFormType } from '@app/icare/db/schema/form/PatientFormType'
import {
  type PatientIdType,
  type PatientStatus,
  PatientStatusEnum,
  type PatientStatusType,
} from '@app/icare/entities/patient/schema/Patient'
import { OrderFormStatusEnum, type OrderFormStatusType } from '@app/icare/entities/patientForm/schema/PatientForm'
import ActiveEditors from '@app/icare/entities/user/components/ActiveEditors'
import { UserInfo } from '@app/icare/entities/user/components/UserInfo'
import { useRouteTour } from '@app/icare/module/tour/useRouteTour'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, F, Q, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { formatDate, formatDateTime } from '@pkg/shared/utils'
import { ButtonLink, FilterButton } from '@pkg/ui/button'
import { LinkIndicatorIcon, SearchInput } from '@pkg/ui/common'
import { FormattedPatientNameAndAddress } from '@pkg/ui/formattedContent'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainer'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Button, Descriptions, Divider, Space, Table, Tour } from 'antd'
import { sql } from 'kysely'
import { jsonObjectFrom } from 'kysely/helpers/sqlite'
import type React from 'react'
import { useMemo, useRef, useState } from 'react'
import { FaTruckRampBox } from 'react-icons/fa6'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'

export const Route = createFileRoute('/_app/_dashboards/orders-stock')({
  component: RouteComponent,
})

const FilterSchema = QueryBaseSchema.extend({
  sortBy: z
    .enum([
      'patientForm.createdBy',
      'patientForm.createdAt',
      'patientForm.updatedBy',
      'patient.personal.lastName',
      'patientForm.form.deliveryTime.from',
      'patientForm.status',
    ])
    .default('patientForm.form.deliveryTime.from')
    .nullish(),
  filters: z
    .object({
      status: Q.in(z.enum(OrderFormStatusEnum.$.zodEnum())).nullish(),
      createdBy: Q.in(z.string()).nullish(),
    })
    .optional(),
})

const PatientStatusValueSchema = z.enum(PatientStatusEnum.$.zodEnum())
const INITIAL_FILTERS = {
  'patientForm.status': F.in([OrderFormStatusEnum.delivered_to_stock]),
  'patient.status': F.in([PatientStatusEnum.new, PatientStatusEnum.open]),
}
const useOrdersStockQueryStore = createQueryStore({
  schema: FilterSchema,
  initialState: {
    filters: INITIAL_FILTERS,
  },
})

function RouteComponent() {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const queryStore = useOrdersStockQueryStore(useShallow(s => s))
  const [open, setOpen] = useState(false)

  const ordersStockParams = queryKit.parseQuery(queryStore, { paginationMode: 'offset' })
  const patientStatusFilter = ordersStockParams.filters.find(f => f.field === 'patient.status')
  const patientStatusValues: PatientStatusType[] =
    patientStatusFilter?.operator === 'in' && Array.isArray(patientStatusFilter.value)
      ? patientStatusFilter.value.flatMap(value => {
          const parsed = PatientStatusValueSchema.safeParse(value)
          return parsed.success ? [parsed.data] : []
        })
      : patientStatusFilter?.operator === 'eq'
        ? (() => {
            const parsed = PatientStatusValueSchema.safeParse(patientStatusFilter.value)
            return parsed.success ? [parsed.data] : []
          })()
        : []

  const { data, isFetching, refetch } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('patientForm')
        .selectAll('patientForm')
        .where('patientForm.type', 'in', Object.values(PatientOrderFormType))
        .select(sb =>
          jsonObjectFrom(
            sb
              .selectFrom('patient')
              .select(['id', 'personal', 'contact', 'status', 'address'])
              .whereRef('patient.id', '=', 'patientForm.patientId'),
          ).as('patient'),
        )
        .$if(patientStatusValues.length > 0, qb =>
          qb.where(({ exists, selectFrom }) =>
            exists(
              selectFrom('patient')
                .select('patient.id')
                .whereRef('patient.id', '=', 'patientForm.patientId')
                .where(eb => eb(eb.ref('patient.status', '->>').key('value'), 'in', patientStatusValues)),
            ),
          ),
        )
        .$call(queryKit.filters(ordersStockParams, ['patientForm.status', 'patientForm.createdBy']))
        .$call(queryKit.search(ordersStockParams))
        .$call(queryKit.deleted(ordersStockParams.deleted, db.dynamic.ref('patientForm.deletedAt')))
        .$if(ordersStockParams.sortBy === 'patientForm.form.deliveryTime.from', qb =>
          qb.orderBy(sql`json_extract("patientForm"."form", '$.deliveryTime.from')`, ordersStockParams.sortDir),
        )
        .$if(ordersStockParams.sortBy === 'patient.personal.lastName', qb =>
          qb.orderBy(
            sql`(
              select json_extract("patient"."personal", '$.lastName')
              from "patient"
              where "patient"."id" = "patientForm"."patientId"
            )`,
            ordersStockParams.sortDir,
          ),
        )
        .$if(
          ordersStockParams.sortBy !== 'patientForm.form.deliveryTime.from' &&
            ordersStockParams.sortBy !== 'patient.personal.lastName',
          qb => qb.$call(queryKit.sort(ordersStockParams)),
        )
        .$call(queryKit.paginate(ordersStockParams)),
      params: ordersStockParams,
    }),
  )

  const columns = useColumnDef<ResultTypeFromQueryResult<typeof data>>(
    () => [
      {
        key: 'patient.personal.lastName',
        title: <Trans>Patientenakte</Trans>,
        fixed: 'start',
        sorter: true,
        render: (_, record) =>
          record.patient && (
            <FormattedPatientNameAndAddress
              patient={{
                ...record.patient,
                id: record.patient.id as PatientIdType,
                status: record.patient.status as PatientStatus,
              }}
            />
          ),
      },
      {
        key: 'patientForm.status',
        title: <Trans>Status</Trans>,
        sorter: true,
        render: (_, record) => (
          <FormStatusBadge<OrderFormStatusType>
            value={record.status as OrderFormStatusType}
            statusEnum={OrderFormStatusEnum}
          />
        ),
      },
      {
        key: 'patientForm.form.deliveryTime.from',
        title: <Trans>Späteste Lieferung</Trans>,
        sorter: true,
        render: (_, record) => formatDate(record.form?.deliveryTime?.from),
      },
      {
        key: 'usersEditing',
        title: <Trans>aktuelle Bearbeitende</Trans>,
        sorter: false,
        render: (_, record) => (
          <ActiveEditors
            routeId="/_app/patienten/$id/_edit/_documents/_forms/bedarfsermittlung/$form"
            params={{ id: record.patientId, form: record.id }}
          />
        ),
      },
      {
        key: 'patientForm.id',
        title: t`Aktionen`,
        fixed: 'end',
        sorter: false,
        render: (_, record) => (
          <ButtonLink
            to="/patienten/$id/bedarfsermittlung/$form"
            params={{ id: record.patientId, form: record.id }}
            size="small"
          >
            <Trans>zum Dokument</Trans>
            <LinkIndicatorIcon />
          </ButtonLink>
        ),
      },
    ],
    [],
    useOrdersStockQueryStore,
  )

  const selectedOrders = useMemo(() => {
    const items = data?.data ?? []
    const set = new Set(selectedRowKeys)
    return items.filter(item => set.has(item.id))
  }, [selectedRowKeys, data])

  const canApprove = useMemo(() => {
    return selectedOrders.length > 0 && selectedOrders.every(o => o.status === OrderFormStatusEnum.delivered_to_stock)
  }, [selectedOrders])

  const approve = useMutation({
    mutationFn: async () => {
      for (const order of selectedOrders) {
        if (order.status === OrderFormStatusEnum.delivered_to_stock) {
          await sync.mutate({
            ...order,
            status: 'approved',
            statusChangedAt: new Date().toISOString(),
          })
        }
      }
      queryStore.setPage(1)
      await refetch()
      setSelectedRowKeys([])
    },
  })

  const tableRef = useRef<HTMLDivElement>(null)
  const approveButtonRef = useRef<HTMLButtonElement>(null)

  const tour = useRouteTour('orders-stock', [
    {
      title: t`Lagerbestellungen`,
      description: t`Dies ist die Übersicht der Bestellungen im Lager. Jede Zeile kann einzeln ausgewählt werden.`,
      target: () => tableRef.current,
    },
    {
      title: t`Versendet`,
      description: t`Über den Button "Ware ist versendet" kann der Status der markierten Bestellungen direkt geändert werden.`,
      target: () => approveButtonRef.current,
      placement: 'bottom',
    },
  ])

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space separator={<Divider vertical size="small" />} size="small">
            <Space size="small">
              <SearchInput
                size="small"
                value={queryStore.meta.searchRawValue}
                onChange={e => queryStore.setSearch(e.target.value)}
              />
              <FilterButton activeFilterCount={queryStore.meta.activeFilterCount} onClick={() => setOpen(true)} />
            </Space>
            <Button
              ref={approveButtonRef}
              type="primary"
              size="small"
              shape="round"
              ghost
              disabled={!canApprove}
              onClick={() => approve.mutate()}
              loading={approve.isPending}
            >
              <FaTruckRampBox className="me-3" />
              <Trans>Ware ist versendet</Trans>
            </Button>
          </Space>
        ),
      },
    },
    [
      queryStore.meta.searchRawValue,
      queryStore.setSearch,
      queryStore.meta.activeFilterCount,
      setOpen,
      queryStore,
      canApprove,
      approve.isPending,
    ],
  )
  return (
    <QueryFilterWrapper
      queryStore={useOrdersStockQueryStore}
      open={open}
      onOpenChange={setOpen}
      filterConfig={[
        {
          name: 'patient.status',
          label: <Trans>Status der Patientenakte</Trans>,
          mode: 'multiple',
          options: PatientStatusEnum.$.options,
        },
        {
          name: 'patientForm.status',
          label: <Trans>Status der Bedarfsermittlung</Trans>,
          mode: 'multiple',
          options: OrderFormStatusEnum.$.options,
        },
      ]}
    >
      <div ref={tableRef}>
        <Table
          dataSource={data?.data ?? []}
          loading={isFetching}
          columns={columns}
          rowKey="id"
          size="small"
          scroll={{ x: 'max-content' }}
          rowSelection={{
            selectedRowKeys,
            onChange: keys => setSelectedRowKeys(keys),
            getCheckboxProps: record => ({
              disabled: record.status !== OrderFormStatusEnum.delivered_to_stock,
            }),
          }}
          pagination={data}
          onChange={queryStore.onChange}
          expandable={{
            expandedRowRender: record => (
              <Descriptions
                column={1}
                size="small"
                style={{ paddingLeft: 32 }}
                items={[
                  {
                    label: t`Erstellt von`,
                    key: 'createdBy',
                    children: <UserInfo userId={record.createdBy} />,
                  },
                  {
                    label: t`Erstellt am`,
                    key: 'createdAt',
                    children: formatDateTime(record.createdAt),
                  },
                  {
                    label: t`Geändert von`,
                    key: 'updatedBy',
                    children: <UserInfo userId={record.updatedBy} />,
                  },
                  {
                    label: t`Geändert am`,
                    key: 'updatedAt',
                    children: formatDateTime(record.updatedAt),
                  },
                ]}
              />
            ),
          }}
        />
      </div>
      <Tour {...tour} />
    </QueryFilterWrapper>
  )
}
